import { FC } from 'react';
import { Icon, Tabs } from '@kontentino/ui';
import { faFlagSwallowtail } from '@fortawesome/pro-regular-svg-icons/faFlagSwallowtail';
import { faLock } from '@fortawesome/pro-regular-svg-icons/faLock';
import { faThumbtack } from '@fortawesome/pro-regular-svg-icons/faThumbtack';
import { faWavePulse } from '@fortawesome/pro-regular-svg-icons/faWavePulse';
import clsx from 'clsx';
import { PostCommunicationContent } from 'app/modules/posts/modules/post/components/postCommunication/types/postCommunicationContent';
import { UserPermission } from 'constants/userPermission';
import UserPermissionGate from 'components/UserPermissionGate';
import useHasPinnedComments from './hooks/useHasPinnedComments';
import useHasPostVersionsTab from './hooks/useHasPostVersionsTab';
type Props = {
  activeTab: PostCommunicationContent;
  onActiveTabChange(type: PostCommunicationContent): void;
};

const possibleTabs = (hasPinned: boolean, hasPostVersionTab?: boolean) => [
  {
    key: PostCommunicationContent.INTERNAL,
    meta: {
      label: 'Internal',
      htmlAttributes: {
        'data-name': 'post-communication-internal-tab',
        'data-cy': 'post-communication-internal-tab',
      },
      permissions: [UserPermission.POST_COMMUNICATION_INTERNAL_SHOW],
    },
  },
  {
    key: PostCommunicationContent.CLIENT,
    meta: {
      label: 'Client',
      icon: <Icon icon={faLock} />,
      htmlAttributes: {
        'data-name': 'post-communication-client-tab',
        'data-cy': 'post-communication-client-tab',
      },
      permissions: [UserPermission.POST_COMMUNICATION_CLIENT_SHOW],
    },
  },
  {
    key: PostCommunicationContent.PINNED,
    meta: {
      label: 'Pinned',
      icon: <Icon icon={faThumbtack} />,
      htmlAttributes: {
        'data-name': 'post-communication-pinned-tab',
        'data-cy': 'post-communication-pinned-tab',
      },
      permissions: [],
    },
    isHidden: !hasPinned,
  },
  {
    key: PostCommunicationContent.ACTIVITY,
    meta: {
      label: 'Activity',
      icon: <Icon icon={faWavePulse} />,
      htmlAttributes: {
        'data-name': 'post-communication-activity-tab',
        'data-cy': 'post-communication-activity-tab',
      },
      permissions: [UserPermission.POST_COMMUNICATION_INTERNAL_SHOW],
    },
  },

  {
    key: PostCommunicationContent.VERSIONS,
    meta: {
      label: 'Versions',
      icon: <Icon icon={faFlagSwallowtail} />,
      htmlAttributes: {
        'data-name': 'post-communication-versions-tab',
        'data-cy': 'post-communication-versions-tab',
      },
      permissions: [UserPermission.POST_COMMUNICATION_INTERNAL_SHOW],
    },
    isHidden: !hasPostVersionTab,
  },
];

const PostCommunicationTabBar: FC<Props> = ({
  activeTab,
  onActiveTabChange,
}) => {
  const { hasPinned } = useHasPinnedComments();
  const hasPostVersionTab = useHasPostVersionsTab();

  return (
    <Tabs.Root defaultValue={activeTab} value={activeTab}>
      <Tabs.List>
        {possibleTabs(hasPinned, hasPostVersionTab)
          .filter((tab) => !tab.isHidden)
          .map(({ key, meta }) => (
            <UserPermissionGate scopes={meta.permissions} key={`tab-${key}`}>
              <Tabs.Tab
                value={key}
                onClick={() => {
                  onActiveTabChange(key);
                }}
                className={clsx({
                  'tw-ml-auto': key === PostCommunicationContent.ACTIVITY,
                })}
                {...meta.htmlAttributes}
              >
                {meta.icon && <Tabs.Icon>{meta.icon}</Tabs.Icon>}
                {meta.label}
              </Tabs.Tab>
            </UserPermissionGate>
          ))}
      </Tabs.List>
    </Tabs.Root>
  );
};

export default PostCommunicationTabBar;
